import React, { useState, useMemo, useCallback } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@klear/ui/components/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@klear/ui/components/card'
import { Badge } from '@klear/ui/components/badge'
import { Button } from '@klear/ui/components/button'
import { DollarSign, Percent, Building2, TrendingUp, Plus, Pencil } from 'lucide-react'
import { AgGridReact } from 'ag-grid-react'
import { type ColDef, type ICellRendererParams, type ValueGetterParams } from 'ag-grid-community'
import { useKlearAgGridTheme } from '@klear/ui/hooks/use-ag-grid-theme'
import { trpc, type RouterOutputs } from '@klear/ui/lib/trpc'
import { formatBasisPoints } from '@klear/basis-points/boundary'

type FunderRow = RouterOutputs['funder']['list'][number]
type FunderGridRow = {
  id: number
  funderName: string
  funderRate: FunderRow['funderRate']
  supportsIG: boolean
  supportsInsured: boolean
}
type FeeRateRow = RouterOutputs['funderPricing']['listInvoiceOfferFeeRates'][number]
import constant from 'lodash/constant'
import PricingCardDialog from '@/app/funder-pricing/PricingCardDialog'
import RateAllocationDialog from '@/app/funder-pricing/RateAllocationDialog'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

const FunderPricing: React.FC = () => {
  useDocumentTitle('Funder Pricing')
  const [activeTab, setActiveTab] = useState<'pricing' | 'funders'>('pricing')
  const agGridTheme = useKlearAgGridTheme()

  const [pricingCardDialogOpen, setPricingCardDialogOpen] = useState(false)
  const [editingPricingCard, setEditingPricingCard] = useState<FeeRateRow | null>(null)
  const [funderRateDialogOpen, setFunderRateDialogOpen] = useState(false)
  const [editingFunder, setEditingFunder] = useState<FunderRow | null>(null)

  const { data: feeRatesRaw, isLoading: isLoadingRates } = trpc.funderPricing.listInvoiceOfferFeeRates.useQuery()
  const { data: fundersRaw, isLoading: isLoadingFunders } = trpc.funder.list.useQuery({})

  const feeRates = useMemo(() => feeRatesRaw ?? [], [feeRatesRaw])
  const funders = useMemo<FunderRow[]>(() => fundersRaw ?? [], [fundersRaw])

  const handleAddFeeRate = useCallback(() => {
    setEditingPricingCard(null)
    setPricingCardDialogOpen(true)
  }, [])

  const handleEditFeeRate = useCallback((rate: FeeRateRow) => {
    setEditingPricingCard(rate)
    setPricingCardDialogOpen(true)
  }, [])

  const handleEditFunderRate = useCallback(
    (id: number) => {
      const funder = funders.find((row) => row.id === id)
      if (funder == null) return
      setEditingFunder(funder)
      setFunderRateDialogOpen(true)
    },
    [funders]
  )

  const feeRateColumns: ColDef[] = useMemo(
    () => [
      {
        field: 'ratingScale',
        headerName: 'Rating Scale',
        filter: 'agTextColumnFilter',
        cellRenderer: (params: ICellRendererParams) => (
          <Badge variant={params.value === 'IG' ? 'default' : 'secondary'}>{params.value}</Badge>
        )
      },
      {
        field: 'rating',
        headerName: 'Rating',
        filter: 'agTextColumnFilter',
        cellRenderer: (params: ICellRendererParams<FeeRateRow>) => params.value ?? ''
      },
      {
        field: 'subscriptionTierName',
        headerName: 'Subscription Tier',
        filter: 'agTextColumnFilter',
        cellRenderer: (params: ICellRendererParams) => {
          const colors: Record<string, string> = {
            Launch: 'bg-muted text-muted-foreground',
            Grow: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
            Pro: 'bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300'
          }
          return (
            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${colors[params.value] || ''}`}>
              {params.value}
            </span>
          )
        }
      },
      {
        colId: 'annualizedRate',
        headerName: 'Annualized Rate',
        filter: 'agNumberColumnFilter',
        valueGetter: (params: ValueGetterParams<FeeRateRow>) => params.data?.annualizedRate.value,
        cellRenderer: (params: ICellRendererParams) => (
          <span className="font-mono text-right">{formatBasisPoints(params.value)}</span>
        )
      },
      {
        headerName: 'Actions',
        width: 120,
        pinned: 'right',
        lockPosition: true,
        suppressMenu: true,
        suppressMovable: true,
        menuTabs: [],
        valueGetter: constant(null),
        cellStyle: { overflow: 'visible' },
        flex: 0,
        resizable: false,
        sortable: false,
        filter: false,
        cellRenderer: (params: ICellRendererParams<FeeRateRow>) => (
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => handleEditFeeRate(params.data!)} className="h-8 w-8 p-0">
              <Pencil className="h-4 w-4" />
            </Button>
          </div>
        )
      }
    ],
    [handleEditFeeRate]
  )

  const funderRows = useMemo<FunderGridRow[]>(() => {
    return funders.map((funder) => ({
      id: funder.id,
      funderName: funder.organizationName || `Funder ${funder.id || 0}`,
      funderRate: funder.funderRate,
      supportsIG: funder.supportsIG,
      supportsInsured: funder.supportsInsured
    }))
  }, [funders])

  const funderColumns: ColDef[] = useMemo(
    () => [
      {
        field: 'funderName',
        headerName: 'Funder (SPV)',
        width: 300,
        filter: 'agTextColumnFilter',
        cellRenderer: (params: ICellRendererParams) => (
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <span>{params.value}</span>
          </div>
        )
      },
      {
        field: 'funderRate',
        headerName: 'SPV Rate',
        filter: 'agNumberColumnFilter',
        valueGetter: (params: ValueGetterParams<FunderGridRow>) => params.data?.funderRate.value,
        cellRenderer: (params: ICellRendererParams) => (
          <span className="font-mono text-blue-600">{formatBasisPoints(params.value)}</span>
        )
      },
      {
        field: 'supportsIG',
        headerName: 'IG',
        filter: 'agTextColumnFilter',
        valueGetter: (params: ValueGetterParams<FunderGridRow>) => (params.data?.supportsIG ? 'Yes' : 'No'),
        cellRenderer: (params: ICellRendererParams) =>
          params.value === 'Yes' ? <Badge variant="default">Yes</Badge> : <Badge variant="secondary">No</Badge>
      },
      {
        field: 'supportsInsured',
        headerName: 'Insured',
        filter: 'agTextColumnFilter',
        valueGetter: (params: ValueGetterParams<FunderGridRow>) => (params.data?.supportsInsured ? 'Yes' : 'No'),
        cellRenderer: (params: ICellRendererParams) =>
          params.value === 'Yes' ? <Badge variant="default">Yes</Badge> : <Badge variant="secondary">No</Badge>
      },
      {
        headerName: 'Actions',
        width: 120,
        pinned: 'right',
        lockPosition: true,
        suppressMenu: true,
        suppressMovable: true,
        menuTabs: [],
        valueGetter: constant(null),
        cellStyle: { overflow: 'visible' },
        flex: 0,
        resizable: false,
        sortable: false,
        filter: false,
        cellRenderer: (params: ICellRendererParams<FunderGridRow>) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEditFunderRate(params.data!.id)}
            className="h-8 w-8 p-0"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )
      }
    ],
    [handleEditFunderRate]
  )

  const stats = useMemo(() => {
    if (!feeRates || !funders) {
      return { totalRates: 0, igRates: 0, allianzRates: 0, funders: 0, igFunders: 0, insuredFunders: 0 }
    }
    return {
      totalRates: feeRates.length,
      igRates: feeRates.filter((r) => r.ratingScale === 'IG').length,
      allianzRates: feeRates.filter((r) => r.ratingScale === 'Allianz').length,
      funders: funders.length,
      igFunders: funders.filter((f) => f.supportsIG).length,
      insuredFunders: funders.filter((f) => f.supportsInsured).length
    }
  }, [feeRates, funders])

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Funder Pricing</h1>
        <p className="text-muted-foreground">
          Manage annualized rates for invoice discounting based on credit rating and subscription tier
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Fee Rates</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalRates}</div>
            <p className="text-xs text-muted-foreground">
              {stats.igRates} IG • {stats.allianzRates} Allianz
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">IG Rates</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.igRates}</div>
            <p className="text-xs text-muted-foreground">Investment Grade pricing</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Allianz Rates</CardTitle>
            <Percent className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.allianzRates}</div>
            <p className="text-xs text-muted-foreground">Insured pricing</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Funders</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.funders}</div>
            <p className="text-xs text-muted-foreground">
              IG {stats.igFunders} • Insured {stats.insuredFunders}
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'pricing' | 'funders')}>
        <TabsList>
          <TabsTrigger value="pricing">Invoice Offer Fee Rates</TabsTrigger>
          <TabsTrigger value="funders">Funders</TabsTrigger>
        </TabsList>

        <TabsContent value="pricing" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Invoice Offer Fee Rates</CardTitle>
                <CardDescription>
                  Annualized rates by rating scale (IG/Allianz), rating value, and subscription tier
                </CardDescription>
              </div>
              <Button onClick={handleAddFeeRate}>
                <Plus className="h-4 w-4 mr-2" />
                Add Fee Rate
              </Button>
            </CardHeader>
            <CardContent>
              <div style={{ height: 600 }}>
                <AgGridReact
                  theme={agGridTheme}
                  rowData={feeRates}
                  columnDefs={feeRateColumns}
                  defaultColDef={{
                    sortable: true,
                    filter: true,
                    resizable: true
                  }}
                  loading={isLoadingRates}
                  pagination={true}
                  paginationPageSize={25}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="funders" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Funders</CardTitle>
                <CardDescription>
                  Single SPV rate allocation and coverage (IG/Insured) used for all fees
                </CardDescription>
              </div>
              <div className="text-sm text-muted-foreground">
                Edit an SPV to change its rate or which rating scales it supports.
              </div>
            </CardHeader>
            <CardContent>
              <div style={{ height: 500 }}>
                <AgGridReact
                  theme={agGridTheme}
                  rowData={funderRows}
                  columnDefs={funderColumns}
                  defaultColDef={{
                    sortable: true,
                    filter: true,
                    resizable: true
                  }}
                  loading={isLoadingRates || isLoadingFunders}
                  pagination={true}
                  paginationPageSize={25}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <PricingCardDialog
        isOpen={pricingCardDialogOpen}
        onClose={() => {
          setPricingCardDialogOpen(false)
          setEditingPricingCard(null)
        }}
        pricingCard={editingPricingCard}
      />

      <RateAllocationDialog
        isOpen={funderRateDialogOpen}
        onClose={() => {
          setFunderRateDialogOpen(false)
          setEditingFunder(null)
        }}
        funder={editingFunder}
      />
    </div>
  )
}

export default FunderPricing
